import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { css } from 'styled-components';
import mergeToNew from '@insite/client-framework/Common/mergeToNew';
import StyledWrapper from '@insite/client-framework/Common/StyledWrapper';
import WidgetModule from '@insite/client-framework/Types/WidgetModule';
import WidgetProps from '@insite/client-framework/Types/WidgetProps';
import Button, { ButtonPresentationProps } from '@insite/mobius/Button';
import { BaseTheme } from '@insite/mobius/globals/baseTheme';
import { IconPresentationProps } from '@insite/mobius/Icon';
import { breakpointMediaQueries, getProp } from '@insite/mobius/utilities';
import getColor from '@insite/mobius/utilities/getColor';
import InjectableCss from '@insite/mobius/utilities/InjectableCss';
import ADIBannerSlide from '@NA/Components/ADIBannerSlide';
import EmblaCarouselWrapper from '@NA/Components/EmblaCarouselWrapper';
import homePageCarousel from '@NA/Handlers/ADIGetHomePageCarouselHandler';
import { BannerCarouselList, GetBannerCarouselApiParameter, PreviewBannerCarouselParams } from '@NA/Services/GetBannerCarousel';
import CustomApplicationState from '@NA/Store/Data/CustomApplicationState/CustomApplicationState';
import ADIColors from '@NA/Utils/ADIColors';
import { formatPayload } from '@NA/Utils/Utility';

const enum fields {
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
    };
    extendedStyles?: ADIHomePageBannerStyles;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    isLoading: state.ADIGlobalData.homePageCarouselStore.isLoading,
    carouselList: state.ADIGlobalData.homePageCarouselStore.bannerCarousel?.bannerCarouselList,
    isAuthenticated: state.context.session.isAuthenticated,
    userRoles: state.context.session.userRoles,
    loyaltyUserStateStore: state.ADIGlobalData.loyaltyRewardUserStore,
    userNameSession: state.context.session.userName?.toLocaleLowerCase(),
});

const mapDispatchToProps = {
    homePageCarousel,
    clearHomePageCarousel: () => ({ type: "HomePage/ClearHomePageCarousel" }),
};

export interface ADIHomePageBannerStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
}

export const adiHomePageBannerStyles: ADIHomePageBannerStyles = {
    slideshowWrapper: {
        css: css`
            padding: 0;
            position: relative;
            display: flex;
            justify-content: center;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            left: 64px;
            margin-top: -20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            @media all and (max-width: 1700px) {
                left: 40px;
            }
            @media all and (max-width: 1450px) {
                left: 20px;
            }
        `,
    },
    prevArrowButton: {
        css: css`
            transform: rotate(180deg);
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                            outline-color:  ${getProp("theme.focus.color", "#09f")};       
                            outline-style: ${getProp("theme.focus.style", "solid")};
                            outline-width: 0px;
                            `,],
                    "min",);
            }}                  
            }
            &:focus-visible {
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                        outline-color: ${getProp("theme.focus.color", "#09f")};     
                        outline-style: ${getProp("theme.focus.style", "solid")};
                        outline-width: 1px;
                        outline-offset: 1px; `,],
                    "min",);
            }}
            } 
        `,
    },
    iconProps: {
        size: 40,
        color: "primary",
        css: css`
            > svg {
                color: ${getColor("primary")};
            }
            top: -1.875em;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -24px;
            position: absolute;
            width: 100%;
            bottom: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        bottom: 0;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dotButton: {
        css: css`
            min-height: 9px;
            height: 13px;
            width: 13px;
            padding: 0;
            margin: 0 4px;
            border: 1px solid ${ADIColors.blue2};
            border-radius: 50%;
            background: ${ADIColors.white};
            &:hover {
                background: ${ADIColors.white};
            }
            &:active {
                background: ${ADIColors.blue2};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            right: 64px;            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            @media all and (max-width: 1700px) {
                right: 40px;
            }
            @media all and (max-width: 1450px) {
                right: 20px;
            }
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                            outline-color:  ${getProp("theme.focus.color", "#09f")};       
                            outline-style: ${getProp("theme.focus.style", "solid")};
                            outline-width: 0px;
                            `,],
                    "min",);
            }}                  
            }
            &:focus-visible {
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, null,
                    css` 
                        outline-color: ${getProp("theme.focus.color", "#09f")};     
                        outline-style: ${getProp("theme.focus.style", "solid")};
                        outline-width: 1px;
                        outline-offset: 1px; `,],
                    "min",);
            }}
            } 
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

let hasPreviewUrl: any;
if (typeof window !== 'undefined') {
    hasPreviewUrl = window.location.href?.toLowerCase().includes('previewbanner');
}

let urlPayloadData: string[] = [];
if (typeof window !== 'undefined' && hasPreviewUrl) {
    urlPayloadData = formatPayload(window.location.href)
}

class ADIHomePageBanner extends React.Component<Props> {
    UNSAFE_componentWillMount(): void {
        this.LoadCarousel();
    }

    componentDidUpdate(): void {
        this.LoadCarousel();
    }

    LoadCarousel() {

        const { isLoading, carouselList, homePageCarousel, userRoles, isAuthenticated, loyaltyUserStateStore, userNameSession } = this.props;
        const isProRewardsUser = isAuthenticated && userRoles && userRoles.includes("ADIProRewards");
        const invitedStatus = ["A", "B", "O"];

        const isLoyaltyLoading = loyaltyUserStateStore.isLoading;
        const emailLTY = loyaltyUserStateStore.user.email != undefined ? loyaltyUserStateStore.user.email?.toLocaleLowerCase() : ""
        const isRewardInvitedUser = (invitedStatus.indexOf(loyaltyUserStateStore.user.status) > -1) && loyaltyUserStateStore.user.isEnrolled == false && !isProRewardsUser && userNameSession == emailLTY;
        const isRewardEnrolledUser = (invitedStatus.indexOf(loyaltyUserStateStore.user.status) > -1) && loyaltyUserStateStore.user.isEnrolled && (isProRewardsUser || false);
        let ifPreviewBanner: any = hasPreviewUrl;
        const payload: any = urlPayloadData;
        let parameter: GetBannerCarouselApiParameter | PreviewBannerCarouselParams;
        if (ifPreviewBanner) {
            parameter = {
                startDate: payload[3] + ' ' + payload[4],
                isPreview: true,
                pageUrl: "home",
                personaType: payload[0],
                customerDC: payload[1] ? payload[1] : '',
                customerNumber: payload[2] ? payload[2] : '',
                checkRoles: "",
                endDate: ""
            } as PreviewBannerCarouselParams; // PreviewBannerCarouselParams if it's preview
        } else {
            parameter = {
                isPreview: false,
                pageUrl: "home"
            } as GetBannerCarouselApiParameter; // GetBannerCarouselApiParameter when not preview
        }

        const bannerPayload = parameter;
        

        if (Object.keys(bannerPayload).length > 2) {
            homePageCarousel({ ...parameter })
            hasPreviewUrl = false;
        }
        else {
            if (!isLoading && !isLoyaltyLoading && !carouselList && isAuthenticated) {
                homePageCarousel({ isPreview: false, pageUrl: "home", isRewardEnrolledUser, isRewardInvitedUser });
            }
            else if (!isLoading && !carouselList && (!isLoyaltyLoading || !isAuthenticated)) {
                homePageCarousel({ ...parameter });
            }
        }


    }

    componentWillUnmount(): void {
        const { clearHomePageCarousel } = this.props;
        clearHomePageCarousel();
    }

    render() {
        const { fields, extendedStyles, isLoading, carouselList } = this.props;
        const styles = mergeToNew(adiHomePageBannerStyles, extendedStyles);

        const onClick = (link: string | undefined, isNewTab?: boolean) => {
            if (link) {
                if (checkIFUrlIsExternal(link) && isNewTab) {
                    window.open(link, "_blank");
                } else if (checkIFUrlIsExternal(link) && !isNewTab) {
                    window.open(link, "_self");
                } else if (!hasPDFExtension(link) && isNewTab) {
                    window.open(link, "_blank");
                } else if (!hasPDFExtension(link) && !isNewTab) {
                    window.location.href = link;
                }
                else {
                    window.open(link);
                }
            }
        };

        function checkIFUrlIsExternal(url: string) {
            return url.toLowerCase().includes("http://") || url.toLowerCase().includes("https://");
        }
        function hasPDFExtension(url: string) {
            const isPDF = /\.pdf(?:\?.*)?$/i.test(url);
            return isPDF;
        }

        if (!carouselList) return null;

        return (
            <StyledWrapper {...styles.slideshowWrapper}>
                <EmblaCarouselWrapper fields={fields} styles={styles} carouselList={carouselList}>
                    <StyledWrapper {...styles.slideContainerWrapper}>
                        {(carouselList as BannerCarouselList[]).map((slide: BannerCarouselList, index: number) => {
                            return (
                                <ADIBannerSlide
                                    key={index}
                                    slide={slide}
                                    onClick={(url: string, isNewTab: boolean) => onClick(url, isNewTab)}
                                />
                            );
                        })}
                    </StyledWrapper>
                </EmblaCarouselWrapper>
            </StyledWrapper>
        );
    }
}

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ADIHomePageBanner),
    definition: {
        group: "Basic",
        icon: "Banner",
        displayName: "ADI Home Page Banner",
        fieldDefinitions: [
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
        ],
    },
};

export default widgetModule;
